import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner,
  Table,
} from "reactstrap";
import { Line } from "react-chartjs-2";
import Select from "react-select";
import NoteAccuracyPanel, {
  MULTI_RECORD_LABELS,
} from "../components/Analytics/NoteAccuracyPanel";
import CommonEditThemeNotes from "../components/Analytics/CommonEditThemeNotes";
import "./NoteQualityDashboard.css";

const EMPTY_LIST = [];
const EMPTY_OBJECT = {};
const DEFAULT_RANGE_DAYS = 14;
const MIN_COHORT_NOTES = 30;
const COMMON_EDIT_STORAGE_KEY = "comprehendpt_common_edit_analysis";

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultFilters = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - DEFAULT_RANGE_DAYS);

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
    status: "all",
    comprehend: "all",
    multiRecord: "",
    emr: "",
    clinicId: "",
    userIds: [],
    emails: "",
  };
};

const buildAnalyticsRequest = (filters) => {
  const request = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    status: filters.status,
    userIds: [],
  };
  ["emr", "clinicId"].forEach((key) => {
    const value = String(filters[key] || "").trim();
    if (value) request[key] = value;
  });
  // Omitted when "all" so requests and stored common-edit keys match older ones.
  if (filters.comprehend === "used" || filters.comprehend === "not_used") {
    request.comprehend = filters.comprehend;
  }
  if (["any", "classic", "plus", "segmented"].includes(filters.multiRecord)) {
    request.multiRecord = filters.multiRecord;
  }
  const emails = filters.emails?.trim()
    ? filters.emails
        .split(/[,\n]/)
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    : filters.userIds;
  if (emails?.length) {
    const uniqueEmails = [...new Set(emails.map((email) => email.trim().toLowerCase()))].sort();
    if (uniqueEmails.length > 1000) throw new Error("Enter up to 1,000 emails.");
    if (uniqueEmails.some((email) =>
      email.length > 254 || !/^[^\s,@]+@[^\s,@]+\.[^\s,@]+$/.test(email)
    )) {
      throw new Error("Check the email list: each entry must be a valid email address.");
    }
    request.userIds = uniqueEmails;
  } else if (filters.emails?.trim()) {
    throw new Error("Enter at least one email address.");
  }
  return request;
};

const commonEditFilterKey = (filters) =>
  JSON.stringify(buildAnalyticsRequest(filters));

const readStoredCommonEdit = (filters) => {
  try {
    const stored = JSON.parse(localStorage.getItem(COMMON_EDIT_STORAGE_KEY));
    if (stored?.job?.analysisId) {
      const storedFilters = JSON.parse(stored.filterKey);
      if (storedFilters.userId) storedFilters.userIds = [storedFilters.userId];
      return commonEditFilterKey(storedFilters) === commonEditFilterKey(filters) ? stored : null;
    }
    localStorage.removeItem(COMMON_EDIT_STORAGE_KEY);
    return null;
  } catch (_error) {
    return null;
  }
};

const storeCommonEdit = (filters, job) => {
  try {
    localStorage.setItem(
      COMMON_EDIT_STORAGE_KEY,
      JSON.stringify({
        filterKey: commonEditFilterKey(filters),
        job,
      }),
    );
  } catch (_error) {
    // The analysis remains available for the current page session.
  }
};

const formatNumber = (value, maximumFractionDigits = 1) =>
  value == null
    ? "—"
    : Number(value).toLocaleString(undefined, { maximumFractionDigits });

const formatPercent = (value) =>
  value == null ? "—" : `${formatNumber(value)}%`;

const formatMinutes = (value) =>
  value == null ? "—" : `${formatNumber(value)} min`;

const formatObservedDuration = (value, availableNotes) =>
  Number(availableNotes) > 0 && value != null
    ? formatMinutes(value)
    : "Not available";

const formatUsd = (value) => {
  const amount = Number(value || 0);
  return amount > 0 && amount < 0.01 ? "<$0.01" : `$${amount.toFixed(2)}`;
};

const rate = (part, total) =>
  total > 0 ? Math.round((Number(part || 0) / total) * 1000) / 10 : null;

// Accuracy = 100% - text changed. The median of the complement is the complement
// of the median, so this is exact for the backend's medianPctChanged. The same
// complement turns a section's edit rate into its share of notes with no edits.
const percentComplement = (value) =>
  value == null ? null : Math.round((100 - Number(value)) * 10) / 10;

const withAccuracyRates = (row) => ({
  ...row,
  medianAccuracy: percentComplement(row.medianPctChanged),
  noEditRate: percentComplement(row.editRate),
});

const SectionGuide = ({ children }) => (
  <details className="note-intel-guide">
    <summary>What does this mean?</summary>
    <p>{children}</p>
  </details>
);

const GuidedSection = ({ guide, children }) => (
  <div className="note-intel-block">
    <SectionGuide>{guide}</SectionGuide>
    {children}
  </div>
);

// Draws a vertical line at the hovered week (Chart.js 2.x plugin API).
const cursorLinePlugin = {
  id: "noteIntelCursorLine",
  afterDatasetsDraw: (chart) => {
    const active = chart.tooltip?._active;
    if (!active?.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0]._model.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#9aa4b5";
    ctx.stroke();
    ctx.restore();
  },
};

const formatWeek = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatDateLabel = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const buildSelectOptions = (rows) =>
  rows.map(({ value, label = value, notes }) => ({
    value: String(value),
    label: `${label} · ${formatNumber(notes, 0)} notes`,
  }));

const selectedOption = (options, value) => {
  if (!value) return null;
  return (
    options.find((option) => option.value === String(value)) || {
      value: String(value),
      label: String(value),
    }
  );
};

const SortControl = ({ column, label, sort, onSort, textRight }) => {
  const active = sort.key === column;
  const direction = active ? sort.direction : null;

  return (
    <button
      type="button"
      className={`note-intel-sort${textRight ? " note-intel-sort-right" : ""}`}
      onClick={() => onSort(column)}
    >
      <span>{label}</span>
      <span className="note-intel-sort-indicator" aria-hidden="true">
        {direction === "desc" ? "↓" : direction === "asc" ? "↑" : "↕"}
      </span>
    </button>
  );
};

const sortAriaValue = (sort, column) => {
  if (sort.key !== column || !sort.direction) return "none";
  return sort.direction === "desc" ? "descending" : "ascending";
};

const SortHeader = ({
  column,
  label,
  description,
  sort,
  onSort,
  textRight,
  className,
}) => (
  <th
    className={`note-intel-described-header${textRight ? " text-right" : ""}${className ? ` ${className}` : ""}`}
    title={description}
    aria-sort={sortAriaValue(sort, column)}
  >
    <SortControl
      column={column}
      label={label}
      sort={sort}
      onSort={onSort}
      textRight={textRight}
    />
  </th>
);

const GridSortHeader = ({
  column,
  label,
  description,
  sort,
  onSort,
  textRight,
}) => (
  <span
    role="columnheader"
    title={description}
    aria-sort={sortAriaValue(sort, column)}
  >
    <SortControl
      column={column}
      label={label}
      sort={sort}
      onSort={onSort}
      textRight={textRight}
    />
  </span>
);

const sortRows = (rows, sort) => {
  if (!sort.key || !sort.direction) return rows;

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = left.row[sort.key];
      const rightValue = right.row[sort.key];
      if (leftValue == null && rightValue == null) {
        return left.index - right.index;
      }
      if (leftValue == null) return 1;
      if (rightValue == null) return -1;

      const comparison =
        typeof leftValue === "string"
          ? leftValue.localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: "base",
            })
          : Number(leftValue) - Number(rightValue);
      if (comparison === 0) return left.index - right.index;
      return sort.direction === "asc" ? comparison : -comparison;
    })
    .map(({ row }) => row);
};

const nextSort = (current, key, initialDirection = "desc") => {
  if (current.key !== key) return { key, direction: initialDirection };
  if (current.direction === initialDirection) {
    return {
      key,
      direction: initialDirection === "desc" ? "asc" : "desc",
    };
  }
  return { key: null, direction: null };
};

const Metric = ({ label, value, detail, description }) => (
  <div className="note-intel-metric" title={description}>
    <div className="note-intel-metric-label">{label}</div>
    <div className="note-intel-metric-value">{value}</div>
    {detail && <div className="note-intel-metric-detail">{detail}</div>}
  </div>
);

const CoverageRow = ({ label, count, total, detail }) => {
  const coverage = rate(count, total);
  return (
    <div className="note-intel-coverage-row">
      <div className="note-intel-coverage-copy">
        <span>{label}</span>
        <strong>{coverage == null ? "—" : formatPercent(coverage)}</strong>
      </div>
      <div className="note-intel-progress" aria-hidden="true">
        <span style={{ width: `${Math.min(coverage || 0, 100)}%` }} />
      </div>
      <div className="note-intel-coverage-detail">
        {formatNumber(count, 0)} of {formatNumber(total, 0)} {detail}
      </div>
    </div>
  );
};

const EmptyPanel = ({ children }) => (
  <div className="note-intel-empty">
    <div className="note-intel-empty-mark" />
    <div>{children}</div>
  </div>
);

const NoteQualityDashboard = ({ apiClient }) => {
  const defaultFilters = useMemo(buildDefaultFilters, []);
  const analysisGeneration = useRef(0);
  const commonEditStatusInFlight = useRef(false);
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState("");
  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filterError, setFilterError] = useState("");
  const [filterRetry, setFilterRetry] = useState(0);
  const [isDev, setIsDev] = useState(Boolean(apiClient?.isDev));
  const [sectionSort, setSectionSort] = useState({
    key: null,
    direction: null,
  });
  const [noteTypeSort, setNoteTypeSort] = useState({
    key: null,
    direction: null,
  });
  const [usageComparisonSort, setUsageComparisonSort] = useState({
    key: null,
    direction: null,
  });
  const [usageNoteTypeSort, setUsageNoteTypeSort] = useState({
    key: null,
    direction: null,
  });
  const [noteTypeSectionSort, setNoteTypeSectionSort] = useState({
    key: null,
    direction: null,
  });
  const [expandedNoteType, setExpandedNoteType] = useState(null);
  const [noteTypeSections, setNoteTypeSections] = useState(() => new Map());
  const [loadingNoteType, setLoadingNoteType] = useState(null);
  const [noteTypeSectionError, setNoteTypeSectionError] = useState("");
  const [openTheme, setOpenTheme] = useState(null);
  const [commonEditPreview, setCommonEditPreview] = useState(null);
  const [commonEditJob, setCommonEditJob] = useState(null);
  const [commonEditAnalysis, setCommonEditAnalysis] = useState(null);
  const [commonEditLoading, setCommonEditLoading] = useState(false);
  const [commonEditStarting, setCommonEditStarting] = useState(false);
  const [commonEditError, setCommonEditError] = useState("");

  useEffect(() => {
    const handleProfileUpdate = (client) => setIsDev(Boolean(client?.isDev));
    apiClient?.onProfileUpdated?.(handleProfileUpdate);
    handleProfileUpdate(apiClient);

    return () => apiClient?.offProfileUpdated?.(handleProfileUpdate);
  }, [apiClient]);

  const loadAnalytics = useCallback(
    async (nextFilters, revealResults = true) => {
      if (!apiClient?.isDev) return;

      const start = new Date(`${nextFilters.startDate}T00:00:00`);
      const end = new Date(`${nextFilters.endDate}T00:00:00`);
      const days = Math.round((end - start) / 86400000) + 1;
      if (Number.isNaN(days) || days < 1 || days > 366) {
        setError("Choose a valid date range of 366 days or less.");
        return;
      }

      let request;
      try {
        request = buildAnalyticsRequest(nextFilters);
      } catch (validationError) {
        setError(validationError.message);
        return;
      }
      request.includeFilterOptions = false;

      const requestGeneration = ++analysisGeneration.current;
      if (revealResults) {
        setLoadingResults(true);
        setShowResults(false);
      }
      setExpandedNoteType(null);
      setLoadingNoteType(null);
      setNoteTypeSectionError("");

      setLoading(true);
      setError("");
      try {
        const response = await apiClient.JMPost("/v1/notes/analytics", request);
        if (requestGeneration !== analysisGeneration.current) return;
        if (response?.error) throw new Error(response.error);
        if (request.userIds && JSON.stringify(response?.filters?.userIds) !== JSON.stringify(request.userIds)) {
          setError("This backend did not apply the email list. Update the backend before running this search.");
          return;
        }
        if (request.multiRecord && response?.filters?.multiRecord !== request.multiRecord) {
          setError("This backend did not apply the multi-record filter. Update the backend before running this search.");
          return;
        }
        if (request.comprehend && response?.filters?.comprehend !== request.comprehend) {
          setError("This backend did not apply the Comprehend usage filter. Update the backend before running this search.");
          return;
        }
        setData(response);
        setOpenTheme(null);
        setExpandedNoteType(null);
        setNoteTypeSections(new Map());
        setLoadingNoteType(null);
        setNoteTypeSectionError("");
        setCommonEditPreview(null);
        setCommonEditLoading(false);
        setCommonEditStarting(false);
        setCommonEditError("");
        const storedCommonEdit = readStoredCommonEdit(response.filters);
        setCommonEditJob(
          storedCommonEdit?.job?.analysisId ? storedCommonEdit.job : null,
        );
        setCommonEditAnalysis(null);
        if (revealResults) setShowResults(true);
      } catch (loadError) {
        if (requestGeneration !== analysisGeneration.current) return;
        console.error("Unable to load note analytics:", loadError);
        setError(
          "Unable to load note analytics. Try again or choose a smaller cohort.",
        );
      } finally {
        if (requestGeneration === analysisGeneration.current) {
          setLoading(false);
          if (revealResults) setLoadingResults(false);
        }
      }
    },
    [apiClient],
  );

  useEffect(() => {
    if (!isDev) return;
    const days = (new Date(filters.endDate) - new Date(filters.startDate)) / 86400000 + 1;
    if (!Number.isFinite(days) || days < 1 || days > 366) {
      setLoadingFilters(false);
      return;
    }
    let cancelled = false;
    setLoadingFilters(true);
    setFilterError("");
    apiClient.JMPost("/v1/notes/analytics/filters", {
      startDate: filters.startDate,
      endDate: filters.endDate,
      status: filters.status,
    })
      .then((response) => {
        if (response?.error) throw new Error(response.error);
        if (!cancelled) setFilterOptions(response.filterOptions || {});
      })
      .catch(() => {
        if (!cancelled) setFilterError("Unable to load filter options. You can still enter emails and run an analysis.");
      })
      .finally(() => {
        if (!cancelled) setLoadingFilters(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, isDev, filters.startDate, filters.endDate, filters.status, filterRetry]);

  const updateFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const overview = data?.overview || EMPTY_OBJECT;
  const trend = data?.trend || EMPTY_LIST;
  const noteTypes = data?.noteTypes || EMPTY_LIST;
  const sections = data?.sections || EMPTY_LIST;
  const usageByNoteType = data?.usageBreakdown?.noteTypes || EMPTY_LIST;
  const multiRecordRows = useMemo(
    () =>
      (data?.multiRecordBreakdown || EMPTY_LIST).map((row) => ({
        ...withAccuracyRates(row),
        label: MULTI_RECORD_LABELS[row.type] || row.type,
        identicalRate: rate(row.zeroEditNotes, row.measuredNotes),
        finalizedRate: rate(row.finalizedNotes, row.capturedNotes),
      })),
    [data],
  );
  const commonEditOverall = commonEditPreview?.overall || {};
  const commonEditProgress = commonEditJob?.progress;
  const emrOptions = filterOptions.emrs || EMPTY_LIST;
  const clinicOptions = useMemo(
    () => buildSelectOptions(filterOptions.clinicIds || EMPTY_LIST),
    [filterOptions.clinicIds],
  );
  const zeroEditRate = rate(overview.zeroEditNotes, overview.measuredNotes);
  const appliedFilters = data?.filters || defaultFilters;
  const usageComparisonRows = useMemo(
    () => [
      {
        cohort: "Comprehend used",
        detail: "First capture or generation → sign-off",
        capturedNotes: overview.comprehendNotes,
        finalizedNotes: overview.finalizedComprehendNotes,
        finalizedRate: rate(
          overview.finalizedComprehendNotes,
          overview.comprehendNotes,
        ),
        signOffAvailableNotes: overview.signOffComprehendNotes,
        medianSignOffMinutes: overview.medianComprehendSignOffMinutes,
      },
      {
        cohort: "Comprehend not used",
        detail: "First capture → sign-off",
        capturedNotes: overview.nonComprehendNotes,
        finalizedNotes: overview.finalizedNonComprehendNotes,
        finalizedRate: rate(
          overview.finalizedNonComprehendNotes,
          overview.nonComprehendNotes,
        ),
        signOffAvailableNotes: overview.signOffNonComprehendNotes,
        medianSignOffMinutes: overview.medianNonComprehendSignOffMinutes,
      },
    ],
    [overview],
  );
  const sortedUsageComparison = useMemo(
    () => sortRows(usageComparisonRows, usageComparisonSort),
    [usageComparisonRows, usageComparisonSort],
  );
  const sortedUsageByNoteType = useMemo(
    () => sortRows(usageByNoteType, usageNoteTypeSort),
    [usageByNoteType, usageNoteTypeSort],
  );

  const sortedSections = useMemo(
    () =>
      sortRows(
        sections.map((section, index) => ({
          ...withAccuracyRates(section),
          rank: index + 1,
        })),
        sectionSort,
      ),
    [sectionSort, sections],
  );
  const sortedNoteTypes = useMemo(
    () =>
      sortRows(
        noteTypes.map((noteType, index) => ({
          ...withAccuracyRates(noteType),
          rank: index + 1,
          finalizedRate: rate(noteType.finalizedNotes, noteType.notes),
          identicalRate: rate(noteType.zeroEditNotes, noteType.notes),
        })),
        noteTypeSort,
      ),
    [noteTypeSort, noteTypes],
  );
  const updateSectionSort = (key) =>
    setSectionSort((current) =>
      nextSort(current, key, key === "rank" ? "asc" : "desc"),
    );
  const updateNoteTypeSort = (key) =>
    setNoteTypeSort((current) =>
      nextSort(current, key, key === "rank" ? "asc" : "desc"),
    );
  const updateUsageComparisonSort = (key) =>
    setUsageComparisonSort((current) => nextSort(current, key));
  const updateUsageNoteTypeSort = (key) =>
    setUsageNoteTypeSort((current) => nextSort(current, key));
  const updateNoteTypeSectionSort = (key) =>
    setNoteTypeSectionSort((current) => nextSort(current, key));

  const toggleNoteType = async (noteType) => {
    if (loading || loadingNoteType) return;
    if (expandedNoteType === noteType) {
      setExpandedNoteType(null);
      return;
    }

    setExpandedNoteType(noteType);
    setNoteTypeSectionSort({ key: null, direction: null });
    setNoteTypeSectionError("");
    if (noteTypeSections.has(noteType)) return;

    const request = {
      ...buildAnalyticsRequest(appliedFilters),
      noteType,
      includeFilterOptions: false,
    };
    setLoadingNoteType(noteType);
    const requestGeneration = analysisGeneration.current;
    try {
      const response = await apiClient.JMPost("/v1/notes/analytics", request);
      if (requestGeneration !== analysisGeneration.current) return;
      if (response?.error) throw new Error(response.error);
      setNoteTypeSections((current) => {
        const next = new Map(current);
        next.set(noteType, response?.sections || EMPTY_LIST);
        return next;
      });
    } catch (loadError) {
      if (requestGeneration !== analysisGeneration.current) return;
      console.error("Unable to load note-type sections:", loadError);
      setNoteTypeSectionError("Unable to load sections for this note type.");
    } finally {
      if (requestGeneration === analysisGeneration.current) {
        setLoadingNoteType(null);
      }
    }
  };

  const loadCommonEditPreview = async () => {
    const requestGeneration = analysisGeneration.current;
    setCommonEditLoading(true);
    setCommonEditError("");
    try {
      const response = await apiClient.JMPost(
        "/v1/notes/common-edits/preview",
        buildAnalyticsRequest(appliedFilters),
      );
      if (requestGeneration !== analysisGeneration.current) return;
      if (response?.error) throw new Error(response.error);
      setCommonEditPreview(response);
    } catch (previewError) {
      if (requestGeneration !== analysisGeneration.current) return;
      console.error("Unable to estimate common-edit analysis:", previewError);
      setCommonEditError("Unable to estimate this analysis.");
    } finally {
      if (requestGeneration === analysisGeneration.current) {
        setCommonEditLoading(false);
      }
    }
  };

  const startCommonEditAnalysis = async () => {
    const requestGeneration = analysisGeneration.current;
    setCommonEditStarting(true);
    setCommonEditError("");
    try {
      const response = await apiClient.JMPost(
        "/v1/notes/common-edits/start",
        buildAnalyticsRequest(appliedFilters),
      );
      if (requestGeneration !== analysisGeneration.current) return;
      if (response?.error) throw new Error(response.error);
      const job = {
        analysisId: response.analysisId,
        status: response.status,
        progress: null,
        cost: commonEditPreview?.cost,
      };
      setCommonEditJob(job);
      setCommonEditAnalysis(null);
      storeCommonEdit(appliedFilters, job);
    } catch (startError) {
      if (requestGeneration !== analysisGeneration.current) return;
      console.error("Unable to start common-edit analysis:", startError);
      setCommonEditError(startError.message || "Unable to start analysis.");
    } finally {
      if (requestGeneration === analysisGeneration.current) {
        setCommonEditStarting(false);
      }
    }
  };

  const refreshCommonEditAnalysis = useCallback(
    async (analysisId) => {
      if (commonEditStatusInFlight.current) return;
      commonEditStatusInFlight.current = true;
      const requestGeneration = analysisGeneration.current;
      try {
        const response = await apiClient.JMPost(
          "/v1/notes/common-edits/status",
          { analysisId },
        );
        if (requestGeneration !== analysisGeneration.current) return;
        const terminalStatuses = ["failed"];
        if (response?.error && !terminalStatuses.includes(response.status)) {
          throw new Error(response.error);
        }
        setCommonEditError("");
        const job = {
          analysisId: response.analysisId,
          status: response.status,
          progress: response.progress,
          cost: response.cost,
        };
        if (terminalStatuses.includes(response.status)) {
          setCommonEditJob(null);
          try {
            localStorage.removeItem(COMMON_EDIT_STORAGE_KEY);
          } catch (_error) {
            // The failed job is still cleared for the current page session.
          }
          setCommonEditError(
            response.error || "The analysis did not complete.",
          );
        } else if (response.status === "completed" && !Array.isArray(response.themes)) {
          setCommonEditJob(null);
          try {
            localStorage.removeItem(COMMON_EDIT_STORAGE_KEY);
          } catch (_error) {
            // The stale job is still cleared for the current page session.
          }
          setCommonEditError(
            "This saved analysis is from an older version. Estimate the cost and run it again.",
          );
        } else if (response.status === "completed") {
          setCommonEditJob(job);
          setCommonEditAnalysis(response);
          storeCommonEdit(appliedFilters, job);
        } else {
          setCommonEditJob(job);
          storeCommonEdit(appliedFilters, job);
        }
      } catch (statusError) {
        if (requestGeneration !== analysisGeneration.current) return;
        console.error("Unable to refresh common-edit analysis:", statusError);
        setCommonEditError("Unable to refresh the analysis status.");
      } finally {
        commonEditStatusInFlight.current = false;
      }
    },
    [apiClient, appliedFilters],
  );

  useEffect(() => {
    const analysisId = commonEditJob?.analysisId;
    const status = commonEditJob?.status;
    if (
      !showResults ||
      !analysisId ||
      commonEditAnalysis ||
      status === "failed"
    ) {
      return undefined;
    }

    refreshCommonEditAnalysis(analysisId);
    const interval = window.setInterval(
      () => refreshCommonEditAnalysis(analysisId),
      30000,
    );
    return () => window.clearInterval(interval);
  }, [
    commonEditAnalysis,
    commonEditJob?.analysisId,
    commonEditJob?.status,
    refreshCommonEditAnalysis,
    showResults,
  ]);

  const trendChart = useMemo(
    () => ({
      labels: trend.map((week) => formatWeek(week.week)),
      datasets: [
        {
          label: "Accuracy",
          data: trend.map((week) => percentComplement(week.medianPctChanged)),
          borderColor: "#4f46e5",
          backgroundColor: "rgba(79, 70, 229, 0.08)",
          pointBackgroundColor: "#4f46e5",
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          fill: false,
          lineTension: 0.24,
          spanGaps: true,
        },
        {
          label: "Notes with no observed edits",
          data: trend.map((week) =>
            rate(week.zeroEditNotes, week.measuredNotes),
          ),
          borderColor: "#0f766e",
          backgroundColor: "rgba(15, 118, 110, 0.08)",
          pointBackgroundColor: "#0f766e",
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          borderDash: [5, 4],
          fill: false,
          lineTension: 0.24,
          spanGaps: true,
        },
      ],
    }),
    [trend],
  );

  const percentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 8,
        usePointStyle: true,
        padding: 20,
        fontColor: "#4b5563",
      },
    },
    hover: { mode: "index", intersect: false },
    tooltips: {
      mode: "index",
      intersect: false,
      backgroundColor: "#111827",
      titleFontColor: "#ffffff",
      bodyFontColor: "#ffffff",
      cornerRadius: 6,
      xPadding: 12,
      yPadding: 10,
      callbacks: {
        title: (items) =>
          items.length
            ? `Week of ${formatDateLabel(trend[items[0].index]?.week)}`
            : "",
        label: (item, chartData) =>
          `${chartData.datasets[item.datasetIndex].label}: ${formatPercent(item.yLabel)}`,
        footer: (items) =>
          items.length
            ? `${formatNumber(trend[items[0].index]?.measuredNotes, 0)} notes with recorded accuracy`
            : "",
      },
    },
    scales: {
      xAxes: [
        {
          gridLines: { display: false },
          ticks: { fontColor: "#7c8799" },
        },
      ],
      yAxes: [
        {
          gridLines: { color: "#edf0f5", drawBorder: false },
          ticks: {
            beginAtZero: true,
            max: 100,
            fontColor: "#7c8799",
            callback: (value) => `${value}%`,
          },
        },
      ],
    },
  };

  if (!isDev) {
    return <Alert color="warning">Developer access is required.</Alert>;
  }

  return (
    <div className="note-intel-dashboard">
      <header className="note-intel-header">
        <div>
          <h2>Note edit analytics</h2>
        </div>
        <div className="note-intel-window">
          <span className="note-intel-live-dot" />
          <div>
            <span>Analysis window</span>
            <strong>
              {formatDateLabel(appliedFilters.startDate)} –{" "}
              {formatDateLabel(appliedFilters.endDate)}
            </strong>
          </div>
        </div>
      </header>

      <section className="note-intel-filter-panel">
        <div className="note-intel-section-heading">
          <h3>Cohort</h3>
          <div className="note-intel-filter-note">
            Internal developer accounts excluded
          </div>
        </div>
        <Form
          className="note-intel-filter-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (loadingNoteType) return;
            loadAnalytics(filters, true);
          }}
        >
          <div className="note-intel-filter-row note-intel-filter-row-primary">
            <FormGroup>
              <Label for="note-quality-start">Start</Label>
              <Input
                id="note-quality-start"
                type="date"
                value={filters.startDate}
                onChange={(event) =>
                  updateFilter("startDate", event.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="note-quality-end">End</Label>
              <Input
                id="note-quality-end"
                type="date"
                value={filters.endDate}
                onChange={(event) =>
                  updateFilter("endDate", event.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="note-quality-status">Note status</Label>
              <Input
                id="note-quality-status"
                type="select"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                <option value="all">All notes</option>
                <option value="finalized">Signed off only</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="note-quality-emr">EMR</Label>
              <Input
                id="note-quality-emr"
                type="select"
                value={filters.emr}
                onChange={(event) => updateFilter("emr", event.target.value)}
              >
                <option value="">All</option>
                {emrOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="note-intel-filter-row note-intel-filter-row-secondary">
            <FormGroup>
              <Label for="note-quality-clinic">Clinic</Label>
              <Select
                inputId="note-quality-clinic"
                classNamePrefix="note-intel-select"
                isClearable
                options={clinicOptions}
                value={selectedOption(clinicOptions, filters.clinicId)}
                placeholder="All"
                onChange={(option) =>
                  updateFilter("clinicId", option?.value || "")
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="note-quality-comprehend">Comprehend usage</Label>
              <Input
                id="note-quality-comprehend"
                type="select"
                value={filters.comprehend}
                onChange={(event) =>
                  updateFilter("comprehend", event.target.value)
                }
              >
                <option value="all">All notes</option>
                <option value="used">Comprehend used</option>
                <option value="not_used">Comprehend not used</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="note-quality-multi-record">Multi-record</Label>
              <Input
                id="note-quality-multi-record"
                type="select"
                value={filters.multiRecord}
                onChange={(event) =>
                  updateFilter("multiRecord", event.target.value)
                }
              >
                <option value="">All notes</option>
                <option value="any">Any multi-record</option>
                <option value="classic">{MULTI_RECORD_LABELS.classic}</option>
                <option value="plus">{MULTI_RECORD_LABELS.plus}</option>
                <option value="segmented">{MULTI_RECORD_LABELS.segmented}</option>
              </Input>
            </FormGroup>
            <Button
              color="primary"
              className="note-intel-run"
              type="submit"
              disabled={loading || Boolean(loadingNoteType)}
            >
              {loading ? <Spinner size="sm" /> : "Run analysis"}
            </Button>
          </div>
          <FormGroup>
            <Label for="note-quality-emails">Clinician emails</Label>
            <Input
              id="note-quality-emails"
              type="textarea"
              rows={3}
              value={filters.emails}
              placeholder="alice@example.com, bob@example.com"
              aria-describedby="note-quality-emails-help"
              onChange={(event) => setFilters((current) => ({
                ...current, emails: event.target.value, userIds: [],
              }))}
            />
          </FormGroup>
        </Form>
        {loadingFilters && <div role="status"><Spinner size="sm" /> Loading filter options…</div>}
        {filterError && <Alert color="warning">
          {filterError}{" "}
          <Button type="button" size="sm" onClick={() => setFilterRetry((value) => value + 1)}>
            Retry filters
          </Button>
        </Alert>}
      </section>

      {error && <Alert color="danger">{error}</Alert>}

      {loadingResults && (
        <div className="note-intel-loading">
          <Spinner />
          <span>Building analysis cohort…</span>
        </div>
      )}

      {data && showResults && (
        <div className="note-intel-results">
          {appliedFilters.comprehend === "not_used" && (
            <Alert color="info">
              Accuracy, text-change, and common-edit metrics compare Comprehend's
              draft to the final note, so they are empty for notes where
              Comprehend was not used.
            </Alert>
          )}
          <GuidedSection guide="These numbers show how much clinicians change Comprehend's draft before signing. Accuracy is how much of a typical note clinicians left unchanged, and No observed edits is the share of notes signed exactly as Comprehend wrote them. Higher is better for both. Only notes where Comprehend was used and we captured both the draft and the final text are counted.">
          <section className="note-intel-metrics" aria-label="Key metrics">
            <Metric
              label="Notes with recorded accuracy"
              value={formatNumber(overview.measuredNotes, 0)}
              detail={`${formatNumber(overview.capturedNotes, 0)} captured notes`}
            />
            <Metric
              label="Accuracy"
              description="100% minus the median percentage of text changed between the AI-generated and final note."
              value={formatPercent(percentComplement(overview.medianPctChanged))}
              detail={`${formatNumber(overview.measuredNotes, 0)} notes with recorded accuracy`}
            />
            <Metric
              label="No observed edits"
              value={formatPercent(zeroEditRate)}
              detail={`${formatNumber(overview.zeroEditNotes, 0)} text-identical snapshots`}
            />
          </section>
          </GuidedSection>

          <GuidedSection guide="Coverage shows how much of the captured data the numbers above rest on. Used Comprehend is the share of captured notes where Comprehend generated text, and Signed off is the share with an observed EMR sign-off. Low coverage means the results describe only part of the practice, so read them with more caution.">
          <section className="note-intel-readiness">
            <div className="note-intel-readiness-heading">
              <h3>Coverage</h3>
            </div>
            <div className="note-intel-coverage-grid">
              <CoverageRow
                label="Used Comprehend"
                count={overview.comprehendNotes}
                total={overview.capturedNotes}
                detail="captured notes"
              />
              <CoverageRow
                label="Signed off"
                count={overview.finalizedNotes}
                total={overview.capturedNotes}
                detail="captured notes"
              />
            </div>
          </section>
          </GuidedSection>

          <NoteAccuracyPanel
            key={JSON.stringify(appliedFilters)}
            apiClient={apiClient}
            filters={appliedFilters}
          />

          <GuidedSection guide="This compares notes written with Comprehend to notes written without it. Signed off shows how many reached sign-off, and time to sign off shows how long a typical note took from the first activity we saw to sign-off. It matters because it shows whether Comprehend changes how quickly clinicians finish notes. Times are wall-clock, not active editing time.">
          <section className="note-intel-panel note-intel-table-panel">
            <div className="note-intel-panel-header">
              <div>
                <h3>Comprehend usage comparison</h3>
              </div>
            </div>
            <div className="table-responsive">
              <Table className="note-intel-table mb-0" hover>
                <thead>
                  <tr>
                    <SortHeader
                      column="cohort"
                      label="Cohort"
                      description="Whether the captured note was linked to a Comprehend generation."
                      sort={usageComparisonSort}
                      onSort={updateUsageComparisonSort}
                    />
                    <SortHeader
                      column="capturedNotes"
                      label="Captured"
                      description="Unique captured notes in this cohort."
                      sort={usageComparisonSort}
                      onSort={updateUsageComparisonSort}
                      textRight
                    />
                    <SortHeader
                      column="finalizedRate"
                      label="Signed off"
                      description="Notes with an observed EMR sign-off, shown as count and percentage of captured notes."
                      sort={usageComparisonSort}
                      onSort={updateUsageComparisonSort}
                      textRight
                    />
                    <SortHeader
                      column="medianSignOffMinutes"
                      label="Time to sign off"
                      description="Median time from the first activity we observed on the note (its first capture or Comprehend generation) to its EMR sign-off. Only signed-off notes count. This is wall-clock time, not active editing time."
                      sort={usageComparisonSort}
                      onSort={updateUsageComparisonSort}
                      textRight
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedUsageComparison.map((row) => (
                    <tr key={row.cohort}>
                      <th scope="row" className="note-intel-row-label">
                        <span>{row.cohort}</span>
                        <small>{row.detail}</small>
                      </th>
                      <td className="text-right">
                        {formatNumber(row.capturedNotes, 0)}
                      </td>
                      <td className="text-right">
                        {formatNumber(row.finalizedNotes, 0)} ·{" "}
                        {formatPercent(row.finalizedRate)}
                      </td>
                      <td className="text-right">
                        {formatObservedDuration(
                          row.medianSignOffMinutes,
                          row.signOffAvailableNotes,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </section>
          </GuidedSection>

          <GuidedSection guide="This shows how often Comprehend is used in each type of note. Note types with low usage are where adoption, or how well the template fits, may need attention.">
          <section className="note-intel-panel note-intel-usage-breakdown">
            <div className="note-intel-panel-header">
              <div>
                <h3>Comprehend usage breakdown</h3>
              </div>
            </div>
            <div className="note-intel-usage-block">
              <div className="note-intel-usage-heading">By note type</div>
              <div className="table-responsive note-intel-usage-scroll">
                <Table className="note-intel-table mb-0" hover>
                  <thead>
                    <tr>
                      <SortHeader
                        column="groupValue"
                        label="Note type"
                        description="Note type reported by the EMR."
                        sort={usageNoteTypeSort}
                        onSort={updateUsageNoteTypeSort}
                      />
                      <SortHeader
                        column="capturedNotes"
                        label="Captured"
                        description="Unique notes observed by note capture."
                        sort={usageNoteTypeSort}
                        onSort={updateUsageNoteTypeSort}
                        textRight
                      />
                      <SortHeader
                        column="comprehendNotes"
                        label="Used"
                        description="Captured notes linked to a Comprehend generation."
                        sort={usageNoteTypeSort}
                        onSort={updateUsageNoteTypeSort}
                        textRight
                      />
                      <SortHeader
                        column="usageRate"
                        label="Usage"
                        description="Percentage of captured notes linked to a Comprehend generation."
                        sort={usageNoteTypeSort}
                        onSort={updateUsageNoteTypeSort}
                        textRight
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsageByNoteType.map((row) => (
                      <tr key={row.groupValue}>
                        <th scope="row">{row.groupValue}</th>
                        <td className="text-right">
                          {formatNumber(row.capturedNotes, 0)}
                        </td>
                        <td className="text-right">
                          {formatNumber(row.comprehendNotes, 0)}
                        </td>
                        <td className="text-right">
                          {formatPercent(row.usageRate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </section>
          </GuidedSection>

          <GuidedSection guide="This compares accuracy across the ways multi-record splits one recording between several patients' notes: classic multi-record, MultiRecord++, and segmented with extra context. Only notes whose visit was recorded alongside at least one other visit count. It matters because a note is only as accurate as the transcript it was given, so a weaker split shows up here as lower accuracy. Classic and MultiRecord++ can only be told apart for notes captured since September 25, 2026; older ones are grouped as Classic or ++.">
          <section className="note-intel-panel note-intel-table-panel">
            <div className="note-intel-panel-header">
              <div>
                <h3>Multi-record</h3>
              </div>
            </div>
            <div className="table-responsive">
              <Table className="note-intel-table mb-0" hover>
                <thead>
                  <tr>
                    <th>Multi-record type</th>
                    <th className="text-right" title="Percentage of captured notes with an observed EMR sign-off.">Signed off</th>
                    <th className="text-right" title="Percentage of notes whose AI and final text are identical.">No observed edits</th>
                    <th className="text-right" title="100% minus the median percentage of text changed between the AI-generated and final note.">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {multiRecordRows.map((row) => (
                    <tr key={row.type}>
                      <th scope="row">{row.label}</th>
                      <td className="text-right">{formatPercent(row.finalizedRate)}</td>
                      <td className="text-right">{formatPercent(row.identicalRate)}</td>
                      <td className="text-right">{formatPercent(row.medianAccuracy)}</td>
                    </tr>
                  ))}
                  {!multiRecordRows.length && (
                    <tr>
                      <td colSpan={4}>
                        <EmptyPanel>No multi-record notes in this cohort.</EmptyPanel>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>
          </GuidedSection>

          <GuidedSection guide="Each point is one week of signed and edited notes. The solid line is accuracy and the dashed line is the share of notes with no observed edits, so both rising means clinicians are changing less. It matters because it shows whether note quality is improving or slipping over time. Hover over the chart to compare both values for a week.">
          <section className="note-intel-panel note-intel-trend-panel">
            <div className="note-intel-panel-header">
              <div>
                <h3>Weekly edit trends</h3>
                <p>
                  Accuracy and share of notes with no observed edits
                </p>
              </div>
              <div className="note-intel-sample-badge">
                {formatNumber(overview.measuredNotes, 0)} notes with recorded
                accuracy
              </div>
            </div>
            <div className="note-intel-chart">
              {trend.length ? (
                <Line
                  data={trendChart}
                  options={percentChartOptions}
                  plugins={[cursorLinePlugin]}
                />
              ) : (
                <EmptyPanel>No weekly observations in this cohort.</EmptyPanel>
              )}
            </div>
          </section>
          </GuidedSection>

          <GuidedSection guide="An AI model reads a sample of the changes clinicians made and groups the ones that keep recurring into themes. Clinical edits change medical content, while cosmetic edits only change wording or formatting. It matters because repeated clinical edits point to information Comprehend is missing or getting wrong, and repeated cosmetic edits point to style preferences the instructions could learn. Click a theme to read the notes behind it.">
          <section className="note-intel-panel note-intel-common-edits">
            <div className="note-intel-panel-header">
              <div>
                <h3>Common edits</h3>
              </div>
              <div className="note-intel-sample-badge">
                {commonEditAnalysis
                  ? `${formatNumber(commonEditAnalysis.sampleNotes, 0)} sampled`
                  : commonEditJob
                    ? commonEditJob.status.replaceAll("_", " ")
                    : "On demand"}
              </div>
            </div>

            {commonEditAnalysis ? (
              <div className="note-intel-common-result">
                <div className="note-intel-common-summary">
                  <div>
                    <span>Recurring themes</span>
                    <h4>
                      {commonEditAnalysis.themes.length
                        ? `${formatNumber(commonEditAnalysis.themes.length, 0)} recurring ${commonEditAnalysis.themes.length === 1 ? "theme" : "themes"} in how clinicians edit notes`
                        : "No recurring themes found"}
                    </h4>
                    <p>
                      {commonEditAnalysis.themes.length
                        ? "Click a theme to read the notes behind it."
                        : "No change appeared in at least two of the sampled notes."}
                    </p>
                  </div>
                  <div className="note-intel-common-rates">
                    <div>
                      <span>Clinical</span>
                      <strong>
                        {formatPercent(commonEditAnalysis.clinicalRate)}
                      </strong>
                      <small>
                        {formatNumber(commonEditAnalysis.clinicalNotes, 0)} notes
                      </small>
                    </div>
                    <div>
                      <span>Cosmetic</span>
                      <strong>
                        {formatPercent(commonEditAnalysis.cosmeticRate)}
                      </strong>
                      <small>
                        {formatNumber(commonEditAnalysis.cosmeticNotes, 0)} notes
                      </small>
                    </div>
                    <div>
                      <span>Cost</span>
                      <strong
                        title={`Billed from the token counts OpenAI reported for this run: ${formatNumber(
                          Number(commonEditAnalysis.cost?.estimatedInputTokens || 0) +
                            Number(commonEditAnalysis.cost?.summaryInputTokens || 0),
                          0,
                        )} input, ${formatNumber(
                          Number(commonEditAnalysis.cost?.estimatedOutputTokens || 0) +
                            Number(commonEditAnalysis.cost?.summaryOutputTokens || 0),
                          0,
                        )} output, including the theme grouping.`}
                      >
                        {formatUsd(commonEditAnalysis.cost?.estimatedUsd)}
                      </strong>
                      <small>
                        {commonEditPreview?.cost?.estimatedUsd
                          ? `${formatUsd(commonEditPreview.cost.estimatedUsd)} estimated`
                          : "actual"}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="note-intel-common-definitions">
                  <dl>
                  <div>
                    <dt>Clinical</dt>
                    <dd>
                      The edit changes clinical content: missing, incorrect, or
                      corrected information, measurements, interventions,
                      assessment or plan reasoning, goals, diagnoses, or
                      functional limitations.
                    </dd>
                  </div>
                  <div>
                    <dt>Cosmetic</dt>
                    <dd>
                      The edit changes wording or presentation only: grammar,
                      spelling, punctuation, formatting, reordering, or
                      wording and style preferences.
                    </dd>
                  </div>
                  </dl>
                  <p>
                    An AI model assigns each changed field to the single
                    category that best explains the change. Rates are the share
                    of sampled notes with at least one edit of that type, so a
                    note can count as both.
                  </p>
                </div>

                {commonEditAnalysis.themes.length > 0 && (
                  <div className="note-intel-theme-grid">
                    {commonEditAnalysis.themes.map((theme, index) => (
                      <button
                        type="button"
                        key={theme.name}
                        className="note-intel-theme-card"
                        title={theme.definition}
                        onClick={() => setOpenTheme(theme)}
                      >
                        <span className="note-intel-theme-meta">
                          <span
                            className={`note-intel-edit-type note-intel-edit-type-${theme.editType.toLowerCase()}`}
                          >
                            {theme.editType}
                          </span>
                          <span>#{index + 1} theme</span>
                        </span>
                        <strong>{theme.name} ›</strong>
                        <span className="note-intel-theme-stats">
                          {formatNumber(theme.noteCount, 0)} notes ·{" "}
                          {formatPercent(theme.noteRate)} of sampled notes ·{" "}
                          {formatNumber(theme.clinicianCount, 0)} clinicians
                        </span>
                        {theme.sections?.length > 0 && (
                          <span className="note-intel-theme-stats">
                            Mostly in {theme.sections.join(", ")}
                          </span>
                        )}
                        <span className="note-intel-theme-example">
                          Example: {theme.example}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="note-intel-common-footnote">
                  Themes need at least two notes, and a note can match more than
                  one theme, so percentages may overlap. AI grouping can be
                  imperfect. {formatNumber(commonEditAnalysis.classifiedNotes, 0)}{" "}
                  edited notes classified · {formatNumber(commonEditAnalysis.failedRequests, 0)}
                  {" "}failed requests · {commonEditAnalysis.model}
                </div>
              </div>
            ) : commonEditJob ? (
              <div className="note-intel-common-pending">
                <Spinner size="sm" />
                <div>
                  <strong>
                    {commonEditJob.status.replaceAll("_", " ")}
                  </strong>
                  <span>
                    {commonEditProgress?.total
                      ? `${formatNumber(commonEditProgress.completed, 0)} of ${formatNumber(commonEditProgress.total, 0)} requests complete`
                      : "Preparing the sampled note changes. You can leave this page while the analysis runs."}
                  </span>
                </div>
              </div>
            ) : commonEditPreview ? (
              <div className="note-intel-common-preview">
                <div className="note-intel-common-preview-grid">
                  <div>
                    <span>Eligible notes</span>
                    <strong>
                      {formatNumber(commonEditOverall.populationNotes, 0)}
                    </strong>
                  </div>
                  <div>
                    <span>Stratified sample</span>
                    <strong>{formatNumber(commonEditOverall.sampleNotes, 0)}</strong>
                  </div>
                  <div>
                    <span>Edited sample</span>
                    <strong>
                      {formatNumber(commonEditOverall.editedSampleNotes, 0)}
                    </strong>
                  </div>
                  <div>
                    <span>Clinicians sampled</span>
                    <strong>
                      {formatNumber(commonEditOverall.sampledClinicians, 0)}
                    </strong>
                  </div>
                  <div className="note-intel-common-cost">
                    <span title="Estimated from the sampled note text and expected classifier output. Actual cost can vary.">
                      Estimated cost
                    </span>
                    <strong>{formatUsd(commonEditPreview.cost?.estimatedUsd)}</strong>
                  </div>
                </div>
                <div className="note-intel-common-actions">
                  <span>
                    {Number(commonEditOverall.editedSampleNotes)
                      ? `${formatNumber(
                          commonEditPreview.cost?.estimatedInputTokens,
                          0,
                        )} input tokens · ${commonEditPreview.model} · Standard API`
                      : "No changed note text is available in this sample."}
                  </span>
                  <Button
                    type="button"
                    color="primary"
                    className="note-intel-run"
                    disabled={
                      commonEditStarting ||
                      !Number(commonEditOverall.editedSampleNotes)
                    }
                    title={
                      Number(commonEditOverall.editedSampleNotes)
                        ? undefined
                        : "No changed notes are available to analyze."
                    }
                    onClick={startCommonEditAnalysis}
                  >
                    {commonEditStarting ? (
                      <Spinner size="sm" />
                    ) : (
                      "Analyze common edits"
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="note-intel-common-empty">
                <div>
                  <strong>Classify recurring clinical and cosmetic edits</strong>
                  <span>
                    Uses a stratified sample of up to 2,400 notes from the
                    selected cohort.
                  </span>
                </div>
                <Button
                  type="button"
                  color="secondary"
                  outline
                  disabled={commonEditLoading}
                  onClick={loadCommonEditPreview}
                >
                  {commonEditLoading ? <Spinner size="sm" /> : "Estimate cost"}
                </Button>
              </div>
            )}
            {commonEditError && (
              <Alert color="danger" className="note-intel-common-error">
                {commonEditError}
              </Alert>
            )}
          </section>
          {openTheme && (
            <CommonEditThemeNotes
              apiClient={apiClient}
              theme={openTheme}
              onClose={() => setOpenTheme(null)}
            />
          )}
          </GuidedSection>

          <GuidedSection guide="This compares accuracy across note types. Select a row to see how each section of that note type holds up. Note types need at least 30 notes to appear, so a handful of notes cannot skew the results. Low accuracy points to note types or templates where clinicians rewrite most of what Comprehend wrote.">
          <section className="note-intel-panel note-intel-table-panel">
            <div className="note-intel-panel-header">
              <div>
                <h3>Accuracy by note type</h3>
              </div>
              <div className="note-intel-sample-badge">
                {formatNumber(noteTypes.length, 0)} note types · at least{" "}
                {MIN_COHORT_NOTES} notes each
              </div>
            </div>
            <div className="table-responsive note-intel-scroll-table">
              <Table
                className="note-intel-table note-intel-note-type-table mb-0"
                hover
              >
                <thead>
                  <tr>
                    <SortHeader
                      column="rank"
                      label="Rank"
                      description="Position in the default note-type ranking."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                      className="note-intel-rank"
                    />
                    <SortHeader
                      column="noteType"
                      label="Note type"
                      description="Note type reported by the EMR for the captured note."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                    />
                    <SortHeader
                      column="notes"
                      label="Notes"
                      description="Notes with recorded accuracy in this note-type cohort."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                      textRight
                    />
                    <SortHeader
                      column="finalizedRate"
                      label="Signed off"
                      description="Percentage of notes with an observed EMR sign-off."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                      textRight
                    />
                    <SortHeader
                      column="identicalRate"
                      label="No observed edits"
                      description="Percentage of notes whose AI and final text are identical."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                      textRight
                    />
                    <SortHeader
                      column="medianAccuracy"
                      label="Accuracy"
                      description="100% minus the median percentage of text changed between the AI-generated and final note."
                      sort={noteTypeSort}
                      onSort={updateNoteTypeSort}
                      textRight
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedNoteTypes.map((noteType) => {
                    const expanded = expandedNoteType === noteType.noteType;
                    const typeSections =
                      noteTypeSections.get(noteType.noteType) || EMPTY_LIST;
                    const sortedTypeSections = sortRows(
                      typeSections.map(withAccuracyRates),
                      noteTypeSectionSort,
                    );

                    return (
                      <React.Fragment key={noteType.noteType}>
                        <tr
                          className="note-intel-expandable-row"
                          onClick={() => toggleNoteType(noteType.noteType)}
                        >
                          <td className="note-intel-rank">{noteType.rank}</td>
                          <th scope="row" className="note-intel-note-type-cell">
                            <button
                              type="button"
                              className="note-intel-expand-button"
                              aria-expanded={expanded}
                            >
                              <span aria-hidden="true">
                                {expanded ? "▾" : "▸"}
                              </span>
                              <span
                                className="note-intel-note-type-name"
                                title={noteType.noteType}
                              >
                                {noteType.noteType}
                              </span>
                            </button>
                          </th>
                          <td className="text-right">
                            {formatNumber(noteType.notes, 0)}
                          </td>
                          <td className="text-right">
                            {formatPercent(noteType.finalizedRate)}
                          </td>
                          <td className="text-right">
                            {formatPercent(noteType.identicalRate)}
                          </td>
                          <td className="text-right">
                            {formatPercent(noteType.medianAccuracy)}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="note-intel-drilldown-row">
                            <td
                              colSpan={6}
                              className="note-intel-drilldown-cell"
                            >
                              {loadingNoteType === noteType.noteType ? (
                                <div className="note-intel-drilldown-loading">
                                  <Spinner size="sm" /> Loading sections
                                </div>
                              ) : noteTypeSectionError ? (
                                <EmptyPanel>{noteTypeSectionError}</EmptyPanel>
                              ) : typeSections.length ? (
                                <div className="note-intel-drilldown">
                                  <div className="note-intel-drilldown-heading">
                                    <strong>Section breakdown</strong>
                                    <span>At least {MIN_COHORT_NOTES} notes each</span>
                                  </div>
                                  <div className="note-intel-drilldown-grid note-intel-drilldown-grid-header">
                                    <GridSortHeader
                                      column="section"
                                      label="Section"
                                      description="Captured note section name."
                                      sort={noteTypeSectionSort}
                                      onSort={updateNoteTypeSectionSort}
                                    />
                                    <GridSortHeader
                                      column="noEditRate"
                                      label="No observed edits"
                                      description="Percentage of notes where no field in this section changed."
                                      sort={noteTypeSectionSort}
                                      onSort={updateNoteTypeSectionSort}
                                      textRight
                                    />
                                    <GridSortHeader
                                      column="medianAccuracy"
                                      label="Accuracy"
                                      description="100% minus the median percentage of text changed between the AI-generated and final content in this section."
                                      sort={noteTypeSectionSort}
                                      onSort={updateNoteTypeSectionSort}
                                      textRight
                                    />
                                  </div>
                                  {sortedTypeSections.map((section) => (
                                    <div
                                      className="note-intel-drilldown-grid note-intel-drilldown-grid-row"
                                      key={section.section}
                                    >
                                      <div className="note-intel-drilldown-section">
                                        <strong title={section.section}>
                                          {section.section}
                                        </strong>
                                        <small>
                                          {formatNumber(section.notes, 0)} notes
                                        </small>
                                      </div>
                                      <span>
                                        {formatPercent(section.noEditRate)}
                                      </span>
                                      <span>
                                        {formatPercent(
                                          section.medianAccuracy,
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <EmptyPanel>
                                  No sections meet the {MIN_COHORT_NOTES}-note
                                  threshold for this note type.
                                </EmptyPanel>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!noteTypes.length && (
                    <tr>
                      <td colSpan={6}>
                        <EmptyPanel>
                          No note types meet the {MIN_COHORT_NOTES}-note
                          threshold.
                        </EmptyPanel>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>
          </GuidedSection>

          <GuidedSection guide="This breaks accuracy down by the sections of a note, such as Subjective or Plan. No observed edits is the share of notes where the section was left exactly as Comprehend wrote it. It matters because it shows which parts of the note clinicians trust and which they keep rewriting. Sections need at least 30 notes to appear.">
          <section className="note-intel-panel note-intel-table-panel">
            <div className="note-intel-panel-header">
              <div>
                <h3>Section metrics</h3>
              </div>
              <div className="note-intel-sample-badge">
                {formatNumber(sections.length, 0)} sections · at least{" "}
                {MIN_COHORT_NOTES} notes each
              </div>
            </div>
            <div className="note-intel-panel-coverage">
              <CoverageRow
                label="Notes with section metrics"
                count={overview.sectionReadyNotes}
                total={overview.measuredNotes}
                detail="notes with recorded accuracy"
              />
            </div>
            <div className="table-responsive note-intel-scroll-table">
              <Table className="note-intel-table mb-0" hover>
                <thead>
                  <tr>
                    <SortHeader
                      column="rank"
                      label="Rank"
                      description="Position in the default section ranking."
                      sort={sectionSort}
                      onSort={updateSectionSort}
                      className="note-intel-rank"
                    />
                    <SortHeader
                      column="section"
                      label="Section"
                      description="Captured note section name."
                      sort={sectionSort}
                      onSort={updateSectionSort}
                    />
                    <SortHeader
                      column="notes"
                      label="Notes"
                      description="Notes with recorded accuracy that contain this section."
                      sort={sectionSort}
                      onSort={updateSectionSort}
                      textRight
                    />
                    <SortHeader
                      column="noEditRate"
                      label="No observed edits"
                      description="Percentage of notes where no field in this section changed."
                      sort={sectionSort}
                      onSort={updateSectionSort}
                      textRight
                    />
                    <SortHeader
                      column="medianAccuracy"
                      label="Accuracy"
                      description="100% minus the median percentage of text changed between the AI-generated and final content in this section."
                      sort={sectionSort}
                      onSort={updateSectionSort}
                      textRight
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedSections.map((section) => (
                    <tr key={section.section}>
                      <td className="note-intel-rank">{section.rank}</td>
                      <th scope="row"><span className="note-intel-section-name">{section.section}</span></th>
                      <td className="text-right">
                        {formatNumber(section.notes, 0)}
                      </td>
                      <td className="text-right">
                        {formatPercent(section.noEditRate)}
                      </td>
                      <td className="text-right">
                        {formatPercent(section.medianAccuracy)}
                      </td>
                    </tr>
                  ))}
                  {!sections.length && (
                    <tr>
                      <td colSpan="5">
                        <EmptyPanel>
                          No sections meet the {MIN_COHORT_NOTES}-note
                          threshold.
                        </EmptyPanel>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </section>
          </GuidedSection>

        </div>
      )}
    </div>
  );
};

export default NoteQualityDashboard;
